import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Simple Toast Component
const SuccessToast = ({ message, description, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div 
      className="fixed bottom-6 right-6 z-[10000] animate-slide-up"
      style={{ animation: 'slideUp 0.3s ease-out' }}
    >
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
      <div className="bg-white rounded-xl shadow-2xl border border-gray-200 p-4 flex items-start gap-3 min-w-[300px]">
        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div className="flex-1">
          <p className="font-semibold text-gray-900">{message}</p>
          {description && <p className="text-sm text-gray-500 mt-0.5">{description}</p>}
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

const GuidedTour = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [isVisible, setIsVisible] = useState(true);
  const [showToast, setShowToast] = useState(false);

  const steps = useMemo(() => [
    {
      target: '[data-tour="dashboard"]',
      title: 'Dashboard',
      description: 'Your command center. View key metrics, team activity, clock-in status, and site overview all in one place.',
      position: 'right'
    },
    {
      target: '[data-tour="admin-settings"]',
      title: 'Admin Settings',
      description: 'Configure your organisation settings and customise preferences.',
      position: 'right'
    },
    {
      target: '[data-tour="sites"]',
      title: 'Sites',
      description: 'Add and manage your construction sites.',
      position: 'right'
    },
    {
      target: '[data-tour="team-members"]',
      title: 'Team Members',
      description: 'Invite your workforce and manage roles.',
      position: 'right'
    },
    {
      target: '[data-tour="tasks"]',
      title: 'Tasks',
      description: 'Create, assign, and track tasks for your team. Set priorities, due dates, and monitor completion progress.',
      position: 'right'
    }
  ], []);

  const updateTargetPosition = useCallback(() => {
    const step = steps[currentStep];
    if (!step) return;

    const element = document.querySelector(step.target);
    if (element) {
      // Scroll element into view if not visible
      element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      
      // Wait for scroll to complete before updating position
      setTimeout(() => {
        const rect = element.getBoundingClientRect();
        setTargetRect(rect);
      }, 300);
    }
  }, [currentStep, steps]);

  useEffect(() => {
    updateTargetPosition();
    
    // Update position on scroll or resize
    window.addEventListener('resize', updateTargetPosition);
    window.addEventListener('scroll', updateTargetPosition, true);
    
    return () => {
      window.removeEventListener('resize', updateTargetPosition);
      window.removeEventListener('scroll', updateTargetPosition, true);
    };
  }, [updateTargetPosition]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API}/api/admin/preferences`,
        { has_completed_guided_tour: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Tour completion saved:', response.data);
      
      // Hide tour and show toast
      setIsVisible(false);
      setShowToast(true);
      
      // Trigger welcome modal after toast appears (1 second delay)
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('guidedTourCompleted'));
      }, 1000);
      
      // Delay onComplete to allow toast to show
      setTimeout(() => {
        onComplete && onComplete();
      }, 5500);
    } catch (error) {
      console.error('Failed to save tour completion:', error);
      // Still hide the tour on error
      setIsVisible(false);
      onComplete && onComplete();
    }
  };

  const handleToastClose = () => {
    setShowToast(false);
  };

  const handleSkip = () => {
    handleComplete();
  };

  // Show toast even when tour is not visible
  if (!isVisible || !targetRect) {
    return showToast ? (
      <SuccessToast 
        message="You're all set!" 
        description="You can always revisit settings from the sidebar menu."
        onClose={handleToastClose}
      />
    ) : null;
  }

  const step = steps[currentStep];
  
  // Calculate tooltip position
  const tooltipStyle = {
    top: targetRect.top + targetRect.height / 2,
    left: targetRect.right + 20,
    transform: 'translateY(-50%)'
  };

  // Spotlight padding
  const spotlightPadding = 8;
  const spotlightRect = {
    top: targetRect.top - spotlightPadding,
    left: targetRect.left - spotlightPadding,
    width: targetRect.width + spotlightPadding * 2,
    height: targetRect.height + spotlightPadding * 2,
  };

  return (
    <div className="fixed inset-0 z-[9999]" data-testid="guided-tour">
      {/* Dark overlay with spotlight cutout */}
      <svg className="absolute inset-0 w-full h-full">
        <defs>
          <mask id="spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            <rect
              x={spotlightRect.left}
              y={spotlightRect.top}
              width={spotlightRect.width}
              height={spotlightRect.height}
              rx="8"
              fill="black"
            />
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0, 0, 0, 0.75)"
          mask="url(#spotlight-mask)"
        />
      </svg>

      {/* Spotlight border/glow */}
      <div
        className="absolute rounded-lg ring-4 ring-purple-500 ring-opacity-75 transition-all duration-300 ease-out pointer-events-none"
        style={{
          top: spotlightRect.top,
          left: spotlightRect.left,
          width: spotlightRect.width,
          height: spotlightRect.height,
          boxShadow: '0 0 0 4px rgba(147, 51, 234, 0.3), 0 0 30px rgba(147, 51, 234, 0.4)'
        }}
      />

      {/* Tooltip */}
      <div
        className="absolute bg-white rounded-xl shadow-2xl p-5 w-80 transition-all duration-300 ease-out"
        style={tooltipStyle}
      >
        {/* Arrow pointing to target */}
        <div
          className="absolute w-4 h-4 bg-white transform rotate-45 -left-2"
          style={{ top: '50%', marginTop: '-8px' }}
        />

        {/* Step indicator */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-medium text-purple-600 bg-purple-100 px-2 py-1 rounded-full">
            Step {currentStep + 1} of {steps.length}
          </span>
          <button
            onClick={handleSkip}
            className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
          >
            Skip tour
          </button>
        </div>

        {/* Content */}
        <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
          <span className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center text-white text-sm">
            {currentStep + 1}
          </span>
          {step.title}
        </h3>
        <p className="text-gray-600 text-sm leading-relaxed mb-4">
          {step.description}
        </p>

        {/* Progress dots */}
        <div className="flex justify-center gap-1.5 mb-4">
          {steps.map((_, index) => (
            <div
              key={index}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                index === currentStep
                  ? 'w-6 bg-purple-600'
                  : index < currentStep
                  ? 'w-1.5 bg-purple-400'
                  : 'w-1.5 bg-gray-300'
              }`}
            />
          ))}
        </div>

        {/* Navigation buttons */}
        <div className="flex gap-2">
          {currentStep > 0 && (
            <button
              onClick={handlePrevious}
              className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium text-sm transition-colors"
            >
              Previous
            </button>
          )}
          <button
            onClick={handleNext}
            className="flex-1 px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium text-sm transition-colors"
          >
            {currentStep === steps.length - 1 ? 'Finish' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GuidedTour;
